<template>
  <div>
    <v-container>
      <v-card>
        <v-card-title>
          <v-btn
            class="text-left"
            color="secondary"
            icon
            :loading="promissoryNotes.loading || statuses.loading"
            @click="resetFilters"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
          Listado de pagarés
        </v-card-title>
        <v-divider></v-divider>

        <v-row class="px-4 pt-4" align="center">
          <v-col cols="12" md="6" class="d-flex align-center flex-wrap">
            <label class="text-left mr-4 mb-0"
              >Filtro por estado del pagaré:</label
            >

            <v-switch
              v-for="status in statuses.data"
              :key="status.status_code"
              :input-value="params.status === status.status_code"
              :label="status.description"
              :color="statusColor(status)"
              :loading="statuses.loading"
              :disabled="statuses.loading || promissoryNotes.loading"
              inset
              dense
              hide-details
              class="mt-0 pt-0 mr-4 flex-grow-0"
              @change="selectStatus(status.status_code)"
            />
          </v-col>
          <v-col cols="12" md="3" class="text-left">
            <v-text-field
              v-model="params.search"
              label="Buscar cliente"
              prepend-inner-icon="mdi-account-search"
              clearable
              dense
              outlined
              hide-details
              :disabled="statuses.loading || promissoryNotes.loading"
              @click:clear="resetFilters"
              @keydown.enter.prevent="applyFilters"
            />
          </v-col>
          <v-col cols="12" md="2" class="text-left">
            <v-btn
              color="primary"
              :loading="promissoryNotes.loading"
              @click="applyFilters"
            >
              <v-icon>mdi-magnify</v-icon>
              BUSCAR
            </v-btn>
          </v-col>
        </v-row>

        <v-data-table
          dense
          :headers="headers"
          :items="promissoryNotes.data"
          :items-per-page="50"
          item-key="userId"
          class="elevation-1 text-uppercase"
          :loading="promissoryNotes.loading"
          loading-text="Cargando..."
          :options.sync="options"
          :server-items-length="promissoryNotes.total"
          disable-sort
        >
          <template #[`item.sapId`]="{ item }">
            {{ item.sapId || '—' }}
          </template>
          <template #[`item.promissoryNoteAmount`]="{ item }">
            {{ formatNumber(item.promissoryNoteAmount, '$', 0) }}
          </template>
          <template #[`item.promissoryNoteDate`]="{ item }">
            {{ item.promissoryNoteDate || '—' }}
          </template>
          <template #[`item.promissoryNote`]="{ item }">
            <v-btn
              v-if="item.promissoryNote"
              color="info"
              fab
              x-small
              dark
              :href="`${filesUrl}/${item.promissoryNote}`"
              target="_blank"
            >
              <v-icon>mdi-file-pdf-box</v-icon>
            </v-btn>
            <span v-else>—</span>
          </template>
          <template #[`item.promissoryStatus`]="{ item }">
            <v-chip small dark :color="statusColor(item.promissoryStatus)">
              {{ item.promissoryStatus?.description || '—' }}
            </v-chip>
          </template>
          <template #[`item.actions`]="{ item }">
            <v-btn
              color="warning"
              fab
              x-small
              dark
              @click="editPromissoryNote(item)"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </template>
          <template #[`item.validityDates`]="{ item }">
            <span v-if="item.validityDates && item.validityDates.end">
              {{ item.validityDates.inValidityPeriod }} —
              {{ item.validityDates.end }}
            </span>
            <span v-else>—</span>
          </template>
        </v-data-table>
      </v-card>
    </v-container>
    <update-promissory-note-form
      v-model="promissoryNoteForm.modal"
      :user-id="promissoryNoteForm.userId"
      :customer-id="promissoryNoteForm.customerId"
      :document-id="promissoryNoteForm.documentId"
      :promissory-note-url="promissoryNoteForm.promissoryNoteUrl"
      :promissory-note-amount="promissoryNoteForm.promissoryNoteAmount"
      :promissory-note-date="promissoryNoteForm.promissoryNoteDate"
      :bank-guarantee-name="promissoryNoteForm.bankGuaranteeName"
      @updated="getPromissoryNotes"
    />
  </div>
</template>

<script>
  import axios from '@/plugins/axios';
  import numbersFormats from '@/mixins/numbersFormats';
  import UpdatePromissoryNoteForm from './modals/UpdatePromissoryNoteForm.vue';

  export default {
    name: 'CreditPromissoryNotes',
    components: {
      UpdatePromissoryNoteForm,
    },
    mixins: [numbersFormats],
    data() {
      return {
        filesUrl: process.env.VUE_APP_FILES_URL,
        promissoryNotes: {
          data: [],
          total: 0,
          loading: false,
          error: '',
        },
        statuses: {
          loading: false,
          error: '',
          data: [],
        },
        options: {},
        params: {
          status: null,
          search: null,
        },
        headers: [
          { text: 'Id', value: 'sapId' },
          { text: 'Nombre', value: 'name', class: 'text-uppercase' },
          { text: 'Fecha de firma de pagaré', value: 'promissoryNoteDate' },
          { text: 'Monto de pagaré', value: 'promissoryNoteAmount' },
          { text: 'Aval', value: 'bankGuaranteeName' },
          { text: 'Pagaré', value: 'promissoryNote' },
          { text: 'Estado del pagaré', value: 'promissoryStatus' },
          { text: 'Fechas de validez', value: 'validityDates' },
          { text: 'Acciones', value: 'actions' },
        ],
        promissoryNoteForm: {
          modal: false,
          customerId: null,
          userId: 0,
          documentId: null,
          promissoryNoteUrl: '',
          promissoryNoteAmount: 0,
          promissoryNoteDate: '',
          bankGuaranteeName: '',
        },
      };
    },
    watch: {
      options: {
        handler() {
          this.getPromissoryNotes();
        },
        deep: true,
      },
    },
    mounted() {
      this.getStatuses();
    },
    methods: {
      editPromissoryNote(promissoryNote) {
        this.promissoryNoteForm.modal = true;
        this.promissoryNoteForm.userId = promissoryNote.userId || 0;
        this.promissoryNoteForm.customerId = promissoryNote.sapId || null;
        this.promissoryNoteForm.documentId = promissoryNote.documentId || null;
        this.promissoryNoteForm.promissoryNoteUrl =
          promissoryNote.promissoryNoteUrl || '';
        this.promissoryNoteForm.promissoryNoteAmount = Number(
          promissoryNote.promissoryNoteAmount || 0
        );
        this.promissoryNoteForm.promissoryNoteDate =
          promissoryNote.promissoryNoteDate || '';
        this.promissoryNoteForm.bankGuaranteeName =
          promissoryNote.bankGuaranteeName || '';
      },
      async getPromissoryNotes() {
        this.promissoryNotes.loading = true;
        this.promissoryNotes.error = '';
        try {
          const { page, itemsPerPage } = this.options;
          const params = [];

          if (this.params.status !== null) {
            params.push(`filters[status]=${this.params.status}`);
          }

          if (this.params.search && this.params.search.length > 0) {
            params.push(
              `filters[search]=${encodeURIComponent(this.params.search)}`
            );
          }

          if (itemsPerPage) {
            params.push(`per_page=${itemsPerPage}`);
          }

          if (page && page > 1) {
            params.push(`page=${page}`);
          }

          const query = params.length > 0 ? `?${params.join('&')}` : '';
          const response = await axios.get(`/promissory-notes/all${query}`);
          this.promissoryNotes.data = response.data.data;
          this.promissoryNotes.total = response.data.meta.total;
        } catch (error) {
          this.promissoryNotes.error =
            error.message || 'Error fetching promissory notes';
        } finally {
          this.promissoryNotes.loading = false;
        }
      },
      async getStatuses() {
        this.statuses.loading = true;
        this.statuses.error = '';
        try {
          const response = await axios.get('/promissory-notes/statuses');
          this.statuses.data = response.data;
        } catch (error) {
          this.statuses.error = error.message || 'Error fetching statuses';
        } finally {
          this.statuses.loading = false;
        }
      },
      selectStatus(statusCode) {
        this.params.status =
          this.params.status === statusCode ? null : statusCode;
        this.applyFilters();
      },
      applyFilters() {
        if (this.options.page && this.options.page > 1) {
          this.options.page = 1;
        } else {
          this.getPromissoryNotes();
        }
      },
      resetFilters() {
        this.params.status = null;
        this.params.search = '';
        this.applyFilters();
      },
      statusColor(status) {
        if (!status || !status.color) return 'grey';
        return status.color === 'gray' ? 'grey' : status.color;
      },
    },
  };
</script>
